(() => {
  if (customElements.get('sf-search')) return;
  customElements.define(
    'sf-search',
    class extends HTMLElement {
      connectedCallback() {
        this.abort = new AbortController();
        this.dialog = this.querySelector('[data-search-dialog]');
        this.input = this.querySelector('input[type="search"]');
        this.results = this.querySelector('[data-search-results]');
        this.trigger = this.querySelector('[data-search-open]');
        this.cache = new Map();
        const opts = { signal: this.abort.signal };
        this.trigger.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            this.open();
          },
          opts,
        );
        this.querySelector('[data-search-close]').addEventListener('click', () => this.close(), opts);
        this.input.addEventListener(
          'input',
          () => {
            clearTimeout(this.timer);
            this.fetchAbort?.abort();
            this.timer = setTimeout(() => this.search(this.input.value.trim()), 180);
          },
          opts,
        );
        this.addEventListener(
          'click',
          (event) => {
            const chip = event.target.closest('[data-search-term]');
            if (chip) {
              this.input.value = chip.dataset.searchTerm;
              this.input.focus();
              this.search(this.input.value);
            }
            if (event.target.closest('[data-search-results] a')) this.close(false);
          },
          opts,
        );
        this.dialog.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              this.close();
            }
            if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
            const links = [...this.results.querySelectorAll('a')];
            if (!links.length) return;
            event.preventDefault();
            const index = links.indexOf(document.activeElement);
            if (event.key === 'ArrowDown') links[(index + 1) % links.length].focus();
            else if (index <= 0) this.input.focus();
            else links[index - 1].focus();
          },
          opts,
        );
        this.dialog.addEventListener('cancel', () => this.unlock(), opts);
        this.dialog.addEventListener('close', () => this.unlock(), opts);
        document.addEventListener(
          'pointerdown',
          (event) => {
            if (this.dialog.open && !this.dialog.contains(event.target) && !this.trigger.contains(event.target))
              this.close();
          },
          opts,
        );
      }
      open() {
        if (this.dialog.open) return;
        if (matchMedia('(max-width: 960px)').matches) {
          this.dialog.showModal();
          this.oldOverflow = document.documentElement.style.overflow;
          document.documentElement.style.overflow = 'hidden';
          this.locked = true;
        } else this.dialog.show();
        this.trigger.setAttribute('aria-expanded', 'true');
        this.input.focus();
      }
      unlock() {
        if (this.locked) document.documentElement.style.overflow = this.oldOverflow || '';
        this.locked = false;
        this.trigger.setAttribute('aria-expanded', 'false');
      }
      close(focus = true) {
        this.dialog.close();
        this.unlock();
        if (focus) this.trigger.focus({ preventScroll: true });
      }
      async search(query) {
        this.querySelector('[data-search-popular]').hidden = Boolean(query);
        if (!query) {
          this.results.replaceChildren();
          this.input.setAttribute('aria-expanded', 'false');
          return;
        }
        if (this.dataset.preview === 'true') {
          this.dispatchEvent(
            new CustomEvent('sf:preview-search', { bubbles: true, detail: { query, results: this.results } }),
          );
          this.input.setAttribute('aria-expanded', 'true');
          return;
        }
        if (this.cache.has(query)) {
          this.render(this.cache.get(query), query);
          return;
        }
        this.fetchAbort?.abort();
        this.fetchAbort = new AbortController();
        this.results.setAttribute('aria-busy', 'true');
        try {
          const url = new URL(this.dataset.predictiveUrl, location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('resources[type]', 'product,collection,page,query');
          url.searchParams.set('resources[limit]', '6');
          url.searchParams.set('section_id', 'sf-predictive-search');
          const response = await fetch(url, { signal: this.fetchAbort.signal });
          if (!response.ok) throw new Error('Predictive search unavailable');
          const markup = await response.text();
          if (query !== this.input.value.trim()) return;
          this.cache.set(query, markup);
          if (this.cache.size > 10) this.cache.delete(this.cache.keys().next().value);
          this.render(markup, query);
        } catch (error) {
          if (error.name !== 'AbortError') this.render('', query, true);
        } finally {
          this.results.removeAttribute('aria-busy');
        }
      }
      render(markup, query, failed = false) {
        this.results.replaceChildren();
        const parsed = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-predictive-content]');
        if (parsed) this.results.append(document.importNode(parsed, true));
        if (!this.results.querySelector('a')) {
          const message = document.createElement('p');
          message.textContent = failed ? this.dataset.errorLabel : this.dataset.emptyLabel;
          this.results.append(message);
        }
        const all = document.createElement('a');
        const url = new URL(this.dataset.searchUrl, location.origin);
        url.searchParams.set('q', query);
        all.href = url.pathname + url.search;
        all.textContent = `${this.dataset.allLabel}: ${query}`;
        all.className = 'sf10-search-all';
        this.results.append(all);
        this.input.setAttribute('aria-expanded', 'true');
        this.querySelector('[data-search-status]').textContent = failed
          ? this.dataset.errorLabel
          : `${this.results.querySelectorAll('a').length - 1} ${this.dataset.resultsLabel}`;
      }
      disconnectedCallback() {
        this.unlock();
        this.abort?.abort();
        this.fetchAbort?.abort();
        clearTimeout(this.timer);
      }
    },
  );
})();
